/**
 * Progressive reveal math for large lists.
 *
 * Large count templates (the test fixture has ~1000 items) freeze the editor
 * because every group row mounts a drag-enabled card. Rather than render the
 * whole list in one blocking pass, the editor reveals rows in
 * animation-frame-sized chunks. These pure helpers compute how many rows to
 * show; the component owns the timing (requestAnimationFrame) and state.
 */

// Lists at or below this size render synchronously (already fast — no
// perceptible issue up to ~300-400 items). Above it we reveal progressively.
export const PROGRESSIVE_THRESHOLD = 300;
// Rows shown on the very first paint of a large list.
export const INITIAL_CHUNK = 150;
// Additional rows revealed on each subsequent frame.
export const CHUNK_SIZE = 150;

/**
 * How many rows to actually render given how far the reveal has advanced.
 *
 * - Lists at or below the threshold always render in full.
 * - Above the threshold the result floors at INITIAL_CHUNK, so the first paint
 *   is never empty even before the reveal effect has run a single step.
 * - The result is always clamped to [0, total], so removing items from a
 *   fully-revealed list won't ask for a slice longer than the list.
 *
 * @param {number} total - total number of rows available
 * @param {number} visibleCount - reveal progress tracked by the component
 * @param {{threshold?: number, initialChunk?: number}} [opts]
 * @returns {number}
 */
export function computeEffectiveVisibleCount(total, visibleCount, opts = {}) {
  const threshold = opts.threshold ?? PROGRESSIVE_THRESHOLD;
  const initialChunk = opts.initialChunk ?? INITIAL_CHUNK;
  const safeTotal = Math.max(0, total | 0);
  if (safeTotal <= threshold) return safeTotal;
  return Math.min(Math.max(visibleCount, initialChunk), safeTotal);
}

/**
 * Reveal target after one progressive step, clamped to `total`.
 *
 * @param {number} current - current reveal progress
 * @param {number} total - total number of rows available
 * @param {{initialChunk?: number, chunkSize?: number}} [opts]
 * @returns {number}
 */
export function nextRevealCount(current, total, opts = {}) {
  const initialChunk = opts.initialChunk ?? INITIAL_CHUNK;
  const chunkSize = opts.chunkSize ?? CHUNK_SIZE;
  return Math.min(Math.max(current, initialChunk) + chunkSize, Math.max(0, total | 0));
}
